import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Settings, ExternalLink, AlertCircle, Loader2 } from 'lucide-react'
import { getPlatformIcon, getPlatformLabel } from '@/lib/platform-utils'
import { supabase } from '@/lib/supabase'
import { Content } from '../useContentData'

interface PlatformSectionProps {
    content: Content
}

export function PlatformSection({ content }: PlatformSectionProps) {
    const navigate = useNavigate()
    const [platformConnection, setPlatformConnection] = useState<any>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const fetchPlatformConnection = async () => {
            if (!content.platform_key || !content.brand_id) {
                setLoading(false)
                return
            }

            try {
                // First get the platform ID from the platforms table
                const { data: platform, error: platformError } = await supabase
                    .from('platforms')
                    .select('id')
                    .eq('platform_key', content.platform_key)
                    .single()

                if (platformError || !platform) {
                    console.error('Error fetching platform:', platformError)
                    setLoading(false)
                    return
                }

                // Then get the platform connection settings
                const { data: connection, error: connectionError } = await supabase
                    .from('platform_brand_settings')
                    .select('*')
                    .eq('brand_id', content.brand_id)
                    .eq('platform_id', platform.id)
                    .single()

                if (!connectionError && connection) {
                    setPlatformConnection({
                        ...connection,
                        platform_id: platform.id
                    })
                }
            } catch (error) {
                console.error('Error fetching platform connection:', error)
            } finally {
                setLoading(false)
            }
        }

        fetchPlatformConnection()
    }, [content.platform_key, content.brand_id])

    if (!content.platform_key) {
        return null
    }

    const handleNavigateToSettings = () => {
        if (platformConnection?.platform_id) {
            navigate(`/brands/${content.brand_id}/connections/${platformConnection.platform_id}/settings`)
        }
    }

    const platformLabel = getPlatformLabel(content.platform_key)
    const isConnected = platformConnection?.status === 'CONNECTED'

    return (
        <div className="group relative overflow-hidden bg-gradient-to-br from-blue-50/80 via-indigo-50/60 to-white rounded-2xl border border-blue-200/60 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 hover:scale-105">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-indigo-50/20" />
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-200/30 to-transparent rounded-full -translate-y-12 translate-x-12" />
            <div className="relative">
                <div className="p-4 pb-3 border-b border-blue-100/50">
                    <h4 className="text-base font-bold bg-gradient-to-r from-blue-800 to-indigo-700 bg-clip-text text-transparent flex items-center gap-2">
                        <div className="p-1.5 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-lg shadow-sm group-hover:from-blue-200 group-hover:to-indigo-300 transition-all duration-300">
                            <Settings className="h-4 w-4 text-blue-600 group-hover:text-blue-700 transition-colors duration-300" />
                        </div>
                        Platform
                    </h4>
                </div>
                
                <div className="p-4 space-y-3">
                    <div className="flex items-center gap-3">
                        <div className={`relative flex-shrink-0 ${
                            loading ? 'animate-pulse' : ''
                        }`}>
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-md border-2 border-white/50 ${
                                isConnected 
                                    ? 'bg-gradient-to-br from-blue-100 to-indigo-200' 
                                    : 'bg-gradient-to-br from-gray-100 to-gray-200'
                            }`}>
                                {getPlatformIcon(content.platform_key, "h-6 w-6")}
                            </div>
                            {isConnected && !loading && (
                                <div className="absolute -top-1 -right-1">
                                    <div className="relative">
                                        <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-75" />
                                        <div className="relative bg-green-500 rounded-full p-1">
                                            <svg className="h-2.5 w-2.5 text-white" fill="currentColor" viewBox="0 0 8 8">
                                                <circle cx="4" cy="4" r="3" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">
                                {platformLabel}
                            </div>
                            <div className="text-xs text-gray-600 mt-0.5">
                                {loading ? (
                                    <span className="flex items-center gap-1">
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                        Checking connection...
                                    </span>
                                ) : isConnected ? (
                                    <span className="flex items-center gap-1 text-green-600">
                                        <span className="inline-block w-1.5 h-1.5 bg-green-500 rounded-full" />
                                        Connected
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-1 text-amber-600">
                                        <AlertCircle className="h-3 w-3" />
                                        Not connected
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {platformConnection && (
                        <Button
                            onClick={handleNavigateToSettings}
                            variant="outline"
                            size="sm"
                            className="w-full group bg-white/60 hover:bg-gradient-to-r hover:from-blue-500 hover:to-indigo-600 hover:text-white hover:border-transparent transition-all duration-300 text-xs"
                        >
                            <Settings className="h-3.5 w-3.5 mr-1.5 group-hover:rotate-90 transition-transform duration-300" />
                            Platform Settings
                            <ExternalLink className="h-3 w-3 ml-auto opacity-50" />
                        </Button>
                    )}

                    {!isConnected && !loading && (
                        <div className="p-2.5 bg-amber-50/80 border border-amber-200/60 rounded-lg">
                            <p className="text-xs text-amber-800 leading-relaxed">
                                This platform is not currently connected. Connect it in platform settings to enable publishing.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}